import { pathOr } from 'ramda';
import { Machine, assign } from 'xstate';
import { verifyEmailAddress } from '../../../../core/auth/util';

const descriptions = {
  WELCOME_HAS_USERS: 'Welcome Back! Select an account from below to login.',
  WELCOME: 'Welcome! Please enter your email address to get started.',
  FORGOT_PASSWORD: [
    'Click the Sign In Link below to receive an email with a link to automatically sign you in to your account.',
    'Or click the Reset Password button for an email with a link to reset your password.'
  ],
  CREATE_ACCOUNT:
    'Enter a password to create an account or update an existing one to be used across all Stack Sports products.',
  WELCOME_NEW_USER:
    'Welcome, back! You’ll now be able to use this account across any Stack Sports products.',
  WELCOME_EXISTING_USER:
    'Welcome, back! You’re now able to use your Stack Sports SSO account to log in to this site.',
  THANKS_CHECK_EMAIL: 'Thanks! To continue, please check your email.',
  THANKS_CHECK_EMAIL_LINK:
    'Thanks! To continue, please check your email. Clicking the link in the email will log you in.',
  LOGIN_SUCCESS: 'Login Success! Returning you back to the application.',
  NEW_PASSWORD_REQUIRED: 'New Password Required. Fill out the form below to update your password.',
  EXPIRED_TOKEN:
    'Your token for that account has expired. Please enter your password below to continue.'
};

const setDescription = description => description;

export const LoginMachine = Machine(
  {
    id: 'login',
    initial: 'email',
    context: {
      emailCheck: {},
      users: [],
      params: {},
      buttonLabel: 'Continue',
      buttonDisabled: false,
      description: descriptions.WELCOME
    },
    on: {
      HAS_USERS: '#login.multiple',
      USE_ANOTHER: '#login.email',
      AUTHENTICATE: {
        target: '#login.password.authenticating',
        actions: assign({
          buttonLabel: () => 'Signing In',
          buttonDisabled: () => true
        })
      },
      AUTHENTICATE_LINK: {
        target: '#login.forgotPassword.sending',
        actions: assign({
          buttonLabel: () => 'Sending',
          buttonDisabled: () => true
        })
      },
      AUTHENTICATE_TOKEN: {
        target: '#login.multiple.authenticating',
        actions: assign({
          buttonLabel: () => 'Signing In',
          buttonDisabled: () => true
        })
      },
      FORGOT_PASSWORD: {
        target: '#login.forgotPassword',
        actions: [
          assign({
            buttonLabel: 'Send Sign In Link',
            description: () => setDescription(descriptions.FORGOT_PASSWORD)
          })
        ]
      },
      SEND_RESET_LINK: {
        target: '#login.forgotPassword.sending',
        actions: [
          assign({
            buttonLabel: () => 'Sending',
            buttonDisabled: () => true
          })
        ]
      },
      RESET: {
        target: '#login.email.init',
        actions: [
          assign({
            buttonLabel: 'Continue',
            buttonDisabled: false,
            description: () => setDescription(descriptions.WELCOME)
          })
        ]
      }
    },
    states: {
      email: {
        initial: 'init',
        states: {
          init: {},
          error: {},
          verifying: {
            invoke: {
              id: 'verifyEmail',
              src: async (ctx, event) => verifyEmailAddress(event.email, ctx.params || {}),
              onDone: [
                {
                  cond: (ctx, event) => {
                    const data = event.data || {};
                    return data.sso === false && data.app === false;
                  },
                  target: '#login.newUser',
                  actions: [
                    'resetButton',
                    assign({
                      emailCheck: (ctx, event) => event.data,
                      description: () => setDescription(descriptions.CREATE_ACCOUNT)
                    })
                  ]
                },
                {
                  target: '#login.password',
                  actions: [
                    'resetButton',
                    assign({
                      emailCheck: (ctx, event) => event.data,
                      newUser: (ctx, event) => pathOr(false, ['data', 'sso'], event),
                      description: (ctx, event) => {
                        const data = event.data || {};
                        const { sso, app } = data;
                        if (app === true && sso === false) {
                          return setDescription(descriptions.WELCOME_NEW_USER);
                        }
                        return setDescription(descriptions.WELCOME_EXISTING_USER);
                      }
                    })
                  ]
                }
              ],
              onError: {
                target: '#login.email.error',
                actions: [
                  'resetButton',
                  assign({
                    error: 'There was an error'
                  })
                ]
              }
            }
          }
        },
        on: {
          VERIFY_EMAIL: {
            target: '#login.email.verifying',
            actions: [
              assign({
                buttonLabel: () => 'Verifying',
                buttonDisabled: () => true
              })
            ]
          }
        }
      },
      newUser: {},
      newPassword: {},
      forgotPassword: {
        initial: 'init',
        states: {
          init: {},
          error: {},
          sent: {},
          sending: {
            invoke: {
              id: 'authenticateLink',
              src: 'authenticateLink',
              onDone: {
                target: '#login.forgotPassword.sent',
                actions: [
                  assign({
                    buttonLabel: 'Back',
                    buttonDisabled: false,
                    description: (ctx, event) =>
                      setDescription(
                        event && event.reset && event.reset === true
                          ? descriptions.THANKS_CHECK_EMAIL
                          : descriptions.THANKS_CHECK_EMAIL_LINK
                      )
                  })
                ]
              },
              onError: {
                target: '#login.forgotPassword.sent',
                actions: [
                  assign({
                    buttonLabel: 'Back',
                    buttonDisabled: false,
                    description: (ctx, event) =>
                      setDescription(
                        event && event.reset && event.reset === true
                          ? descriptions.THANKS_CHECK_EMAIL
                          : descriptions.THANKS_CHECK_EMAIL_LINK
                      )
                  })
                ]
              }
            }
          }
        }
      },
      password: {
        initial: 'init',
        states: {
          init: {},
          error: {},
          authenticating: {
            entry: ['setPassword', 'clearErrors'],
            invoke: {
              id: 'authenticate',
              src: 'authenticate',
              onDone: [
                {
                  cond: ctx => pathOr(false, ['emailCheck', 'exists'], ctx) === false,
                  target: '#login.verification',
                  actions: ['resetButton']
                },
                {
                  cond: (ctx, event) => pathOr(false, ['data', 'userTokens', 'accessToken'], event),
                  target: '#login.redirect',
                  actions: [
                    'resetButton',
                    'clearErrors',
                    assign({
                      userTokens: (ctx, event) => event.data.userTokens,
                      description: () => setDescription(descriptions.LOGIN_SUCCESS)
                    })
                  ]
                },
                {
                  target: '#login.newPassword',
                  actions: [
                    'resetButton',
                    assign({
                      userTokens: (ctx, event) => event.data.userTokens,
                      buttonLabel: () => 'Update',
                      description: () => setDescription(descriptions.NEW_PASSWORD_REQUIRED)
                    })
                  ]
                }
              ],
              onError: {
                target: '#login.password.error',
                actions: [
                  'resetButton',
                  assign({
                    error: 'Incorrect Username or Password.'
                  })
                ]
              }
            }
          }
        }
      },
      multiple: {
        initial: 'idle',
        states: {
          idle: {},
          authenticating: {
            invoke: {
              id: 'authenticateToken',
              src: 'authenticateToken',
              onDone: {
                target: '#login.redirect',
                actions: [
                  'resetButton',
                  assign({
                    userTokens: (ctx, event) => event.data.userTokens,
                    description: () => setDescription(descriptions.WELCOME_HAS_USERS)
                  })
                ]
              },
              onError: {
                target: '#login.password.error',
                actions: [
                  'resetButton',
                  assign({
                    emailCheck: () => ({
                      exists: true
                    }),
                    description: () => setDescription(descriptions.EXPIRED_TOKEN)
                  })
                ]
              }
            }
          }
        },
        entry: assign({
          description: () => setDescription(descriptions.WELCOME_HAS_USERS)
        })
      },
      redirect: {},
      verification: {
        entry: assign({
          description: () => []
        })
      }
    }
  },
  {
    actions: {
      setPassword: assign({
        password: (ctx, { data }) => data.password
      }),
      clearErrors: assign({
        errors: () => null
      }),
      resetButton: assign({
        buttonLabel: () => 'Continue',
        buttonDisabled: () => false
      })
    }
  }
);

export default LoginMachine;



// WEBPACK FOOTER //
// ./src/views/sports-connect/pages/Login/machines.js